import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  getFlagsSnapshot,
  loadEffectiveFlags,
  subscribeFlagsSnapshot,
  type EffectiveFlags,
  type FlagsSnapshot,
} from './featureFlagsApi';

/**
 * Hak i komponent do funkcji „do decyzji” za przełącznikiem (decyzja
 * użytkownika 12.10.2026). Wzór: `ModuleGate` — z jedną świadomą różnicą:
 * **gdy odczyt się nie udał, funkcja jest ukryta** (wersja „obecna”).
 * Moduł spółki jest domyślnie włączony, a funkcja do decyzji — domyślnie
 * nieobecna. Rozstrzyga i tak API.
 *
 * **Zanim przełączniki przyjdą** (integracja 12.10.2026): stan „ładowanie”,
 * nie „wyłączony”. Wcześniej hak startował od „wyłączony”, więc w wersji
 * „nowej” przez chwilę stał ekran „obecny” (karta pojazdu, lista zleceń).
 * Teraz przełączniki czytamy raz na sesję, stan trzymamy synchronicznie
 * (`featureFlagsApi.ts`), a do pierwszej odpowiedzi ekrany zależne od wersji
 * pokazują kółko ładowania (`FeatureFlagsReady` w układzie panelu,
 * `FeatureGate` z `loading`, `useFeatureState`).
 */

/** Stan odczytu przełączników; pierwszy odczyt w sesji startuje sam. */
function useFlagsSnapshot(): FlagsSnapshot {
  const snapshot = useSyncExternalStore(subscribeFlagsSnapshot, getFlagsSnapshot);
  const needsLoad = snapshot.status !== 'ready';
  useEffect(() => {
    if (needsLoad) void loadEffectiveFlags();
  }, [needsLoad]);
  return snapshot;
}

/** Stan dla zalogowanego; `null`, dopóki nie przyszedł (albo gdy odczyt się nie udał). */
export function useFeatureFlags(): EffectiveFlags | null {
  return useFlagsSnapshot().state;
}

/** Czy pierwszy odczyt przełączników w sesji jeszcze trwa. */
export function useFeaturesLoading(): boolean {
  return useFlagsSnapshot().status === 'loading';
}

/**
 * Stan funkcji z rozróżnieniem „jeszcze nie wiadomo”: `undefined` —
 * przełączniki się wczytują (pokaż ładowanie, nie wersję obecną); po błędzie
 * odczytu — `false` (wersja „obecna”, bezpiecznie).
 */
export function useFeatureState(key: string): boolean | undefined {
  const snapshot = useFlagsSnapshot();
  if (snapshot.status === 'loading') return undefined;
  return snapshot.state?.flags[key] === true;
}

/** Czy funkcja działa dla zalogowanego. Nieznany klucz, ładowanie i brak odpowiedzi — nie. */
export function useFeature(key: string): boolean {
  return useFeatureState(key) === true;
}

/** Kółko ładowania, zanim przyjdą przełączniki — zamiast wersji „obecnej”. */
export const FeaturesLoading = () => (
  <Box
    data-testid="features-loading"
    role="progressbar"
    aria-label="Wczytywanie wersji aplikacji"
    sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
  >
    <CircularProgress size={28} />
  </Box>
);

/**
 * Sekcja widoczna tylko z włączonym przełącznikiem. Bez niego — `fallback`
 * (zachowanie sprzed zmiany), domyślnie nic. Zanim przełączniki przyjdą —
 * `loading` (domyślnie nic: ani nowa, ani stara wersja).
 */
export const FeatureGate = ({
  feature,
  children,
  fallback = null,
  loading = null,
}: {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
  loading?: ReactNode;
}) => {
  const on = useFeatureState(feature);
  return <>{on === undefined ? loading : on ? children : fallback}</>;
};

/**
 * Treść panelu dopiero po pierwszym odczycie przełączników w sesji (albo po
 * błędzie — wtedy wersja „obecna”). Jeden odczyt na sesję, więc kółko widać
 * raz, przy wejściu; kolejne ekrany rysują się od razu właściwą wersją.
 */
export const FeatureFlagsReady = ({ children }: { children: ReactNode }) =>
  useFeaturesLoading() ? <FeaturesLoading /> : <>{children}</>;
